import { FC, FormEvent } from "react";

import { Slider, Stack, TextField, Typography } from "@mui/material";

import { FilterName } from "@pages/Catalog/components/components";

import { useIsDesktop } from "@hooks/layout";

import { formatTimeSlider } from "@utils/formatTimeSlider";
import get from "lodash.get";

interface ISliderInputProps {
  fieldName: string;
  values: any;
  maxSliderLength: number;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
}

const SliderInput: FC<ISliderInputProps> = ({ fieldName, values, maxSliderLength, setFieldValue, handleSubmit }) => {
  const isDesktop = useIsDesktop();

  const formattedValue = values[fieldName]?.every((el: number) => el === 0) ? [0, maxSliderLength] : values[fieldName];

  const handleChange = (_event: Event, newValue: any) => {
    setFieldValue(fieldName, newValue);
  };

  const handleSetField = (field: string, value: number | number[]) => {
    setFieldValue(field, value);
  };

  const formatSliderBpm = (value: number) => {
    if (!value) {
      return;
    }

    return `${value} BPM`;
  };

  const formatSliderTime = (value: number) => {
    return formatTimeSlider(value);
  };

  return (
    <>
      <Stack width="100%" marginBottom="8px">
        <Slider
          name={fieldName}
          value={formattedValue}
          step={1}
          min={0}
          max={maxSliderLength}
          onChange={(event, value) => {
            handleChange(event, value);
          }}
          onChangeCommitted={(event) => {
            handleSubmit?.(event as FormEvent<HTMLFormElement>);
          }}
          getAriaValueText={(value) => {
            return String(fieldName === "length" ? formatSliderTime(value) : value);
          }}
          valueLabelFormat={(value) => {
            return String(fieldName === "length" ? formatSliderTime(value) : value);
          }}
          valueLabelDisplay="auto"
          sx={{
            color: "#9cd3fd",
            height: 2,
            "& .MuiSlider-track": {
              background: "#9cd3fd",
              border: "none",
              height: 2,
            },
            "& .MuiSlider-rail": {
              background: "rgba(255, 255, 255, 0.35)",
              height: 2,
            },
            "& .MuiSlider-thumb": {
              width: 14,
              height: 14,
              background: "#9cd3fd",
              boxShadow: "0 0 6px rgba(156, 211, 253, 0.4)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 10px rgba(156, 211, 253, 0.6)",
              },
            },
            "& .MuiSlider-valueLabel": {
              background: "rgba(75, 55, 39, 0.95)",
              border: "1px solid rgba(186, 144, 92, 0.25)",
              borderRadius: "8px",
              padding: "4px 10px",
              fontSize: "0.8125rem",
              fontFamily: '"Raleway", sans-serif',
              color: "#fbf4ec",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            },
          }}
        />

        <Stack direction="row" justifyContent="space-between">
          {fieldName === "length" && isDesktop && (
            <>
              <FilterName>{formatTimeSlider(Number(values[fieldName][0]))}</FilterName>
              <FilterName>{formatTimeSlider(Number(values[fieldName][1]) || maxSliderLength)}</FilterName>
            </>
          )}
          {fieldName === "bpm" && isDesktop && (
            <>
              <FilterName>{`${values[fieldName][0]} BPM`}</FilterName>
              <FilterName>{`${values[fieldName][1] || maxSliderLength} BPM`}</FilterName>
            </>
          )}

          {fieldName === "length" && !isDesktop && (
            <Stack spacing="24px" width="100%">
              <TextField
                disabled
                value={formatSliderTime(values[fieldName][1])}
                onChange={(event) =>
                  !isNaN(Number(event.target.value)) &&
                  handleSetField(fieldName, [get(values, fieldName)[1], Number(event.target.value)])
                }
                placeholder={formatTimeSlider(maxSliderLength)}
              />
            </Stack>
          )}
          {fieldName === "bpm" && !isDesktop && (
            <Stack spacing="24px" direction="row" alignItems="center" justifyContent="center">
              <TextField
                disabled
                value={formatSliderBpm(values[fieldName][0])}
                onChange={(event) =>
                  !isNaN(Number(event.target.value)) &&
                  handleSetField(fieldName, [Number(event.target.value), get(values, fieldName)[1]])
                }
                placeholder="0 BPM"
              />
              <Typography color="#FBF4EC" fontSize="1.5rem">
                -
              </Typography>
              <TextField
                disabled
                value={formatSliderBpm(values[fieldName][1])}
                onChange={(event) =>
                  !isNaN(Number(event.target.value)) &&
                  handleSetField(fieldName, [get(values, fieldName)[1], Number(event.target.value)])
                }
                placeholder={formatSliderBpm(maxSliderLength) || "0 BPM"}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default SliderInput;
